import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  FiArrowRight,
  FiChevronDown,
  FiHeart,
  FiMenu,
  FiMinus,
  FiPlus,
  FiSearch,
  FiShoppingBag,
  FiTrash2,
  FiX,
} from "react-icons/fi";
import productData from "../Datas/productData";
import siteConfig, { formatPrice } from "../config/siteConfig";
import { CartContext } from "./CartContext";
import "../css/header.scss";

const shopGroups = [
  {
    title: "Shop all",
    links: [
      ["Best sellers", "/shop?sort=featured"],
      ["New arrivals", "/shop?sort=newest"],
      ["All products", "/shop"],
    ],
  },
  {
    title: "Keyboards",
    links: [
      ["65% keyboards", "/shop?category=Keyboards&q=65"],
      ["75% keyboards", "/shop?category=Keyboards&q=75"],
      ["96% keyboards", "/shop?category=Keyboards&q=96"],
      ["Hall effect", "/shop?category=Keyboards&q=rapid"],
    ],
  },
  {
    title: "Peripherals",
    links: [
      ["Mice", "/shop?category=Mice"],
      ["Mousepads", "/shop?category=Mousepads"],
      ["Keycaps", "/shop?category=Keycaps"],
      ["Accessories", "/shop?category=Accessories"],
    ],
  },
];

const collectionLinks = [
  ["Signal", "/shop?q=Signal"],
  ["Prism", "/shop?q=Prism"],
  ["Tidal", "/shop?q=Tidal"],
  ["Crest", "/shop?q=Crest"],
];

const pageTitles = {
  "/": "Premium Gaming Keyboards",
  "/shop": "Shop Gaming Gear",
  "/cartpage": "Your Bag",
  "/checkout": "Checkout",
  "/paysuccess": "Order Confirmation",
  "/about": "Our Story",
  "/contact": "Contact Support",
  "/shipping": "Shipping & Delivery",
  "/returns": "Returns & Exchanges",
  "/privacy": "Privacy Policy",
  "/terms": "Terms of Service",
  "/cookies": "Cookie Policy",
  "/policies": "Policy Center",
  "/faqs": "Frequently Asked Questions",
  "/payment": "Payment Status",
};

function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    cartItems,
    itemCount,
    wishlistCount,
    removeFromCart,
    updateQuantity,
  } = useContext(CartContext);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const cartLines = useMemo(
    () =>
      cartItems
        .map((item) => ({
          ...item,
          product: productData.find(
            (product) => Number(product.id) === Number(item.id)
          ),
        }))
        .filter((item) => item.product),
    [cartItems]
  );

  const subtotal = cartLines.reduce(
    (total, item) => total + item.product.price * item.quantity,
    0
  );

  useEffect(() => {
    setMobileOpen(false);
    setSearchOpen(false);
    setCartOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    const title = location.pathname.startsWith("/shopDetails/")
      ? "Product"
      : location.pathname.startsWith("/policies/")
        ? "Store Policy"
        : pageTitles[location.pathname] || "Gaming Gear";
    document.title = `${title} | ${siteConfig.wordmark}`;
  }, [location.pathname]);

  useEffect(() => {
    const shouldLock = mobileOpen || searchOpen || cartOpen;
    document.body.classList.toggle("drawer-open", shouldLock);
    return () => document.body.classList.remove("drawer-open");
  }, [cartOpen, mobileOpen, searchOpen]);

  useEffect(() => {
    const openCart = () => setCartOpen(true);
    window.addEventListener("hushcider:open-cart", openCart);
    return () => window.removeEventListener("hushcider:open-cart", openCart);
  }, []);

  const submitSearch = (event) => {
    event.preventDefault();
    const query = searchTerm.trim();
    navigate(query ? `/shop?q=${encodeURIComponent(query)}` : "/shop");
  };

  return (
    <>
      <header className="site-header">
        <div className="announcement-bar" aria-label="Store announcements">
          <span>{siteConfig.announcements[0]}</span>
          <span className="announcement-bar__secondary">
            {siteConfig.announcements[1]}
          </span>
        </div>

        <div className="site-header__nav">
          <button
            type="button"
            className="icon-button site-header__mobile-trigger"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((value) => !value)}
          >
            {mobileOpen ? <FiX /> : <FiMenu />}
          </button>

          <Link className="wordmark" to="/" aria-label={`${siteConfig.brandName} home`}>
            <span className="wordmark__mark" aria-hidden="true">H</span>
            <span className="wordmark__name">{siteConfig.wordmark}</span>
          </Link>

          <nav className="site-nav" aria-label="Primary navigation">
            <div className="site-nav__item site-nav__item--has-menu">
              <NavLink to="/shop">
                Shop <FiChevronDown aria-hidden="true" />
              </NavLink>
              <div className="mega-menu">
                <div className="mega-menu__groups">
                  {shopGroups.map((group) => (
                    <div key={group.title}>
                      <p>{group.title}</p>
                      {group.links.map(([label, path]) => (
                        <Link key={label} to={path}>
                          {label}
                        </Link>
                      ))}
                    </div>
                  ))}
                </div>
                <Link className="mega-menu__feature" to="/shop?sort=newest">
                  <img src="/img/nav-prism-keyboard.png" alt="Pale blue compact keyboard" />
                  <span>NEW DROP</span>
                  <strong>Prism Series</strong>
                </Link>
              </div>
            </div>

            <div className="site-nav__item site-nav__item--has-menu">
              <Link to="/shop">
                Collections <FiChevronDown aria-hidden="true" />
              </Link>
              <div className="mini-menu">
                {collectionLinks.map(([label, path]) => (
                  <Link key={label} to={path}>
                    {label}
                  </Link>
                ))}
              </div>
            </div>

            <NavLink to="/about">About</NavLink>
            <NavLink to="/contact">Support</NavLink>
            <div className="site-nav__item site-nav__item--has-menu">
              <button className="site-nav__more" type="button">
                More <FiChevronDown aria-hidden="true" />
              </button>
              <div className="mini-menu">
                <Link to="/faqs">FAQs</Link>
                <Link to="/policies">Policy center</Link>
                <Link to="/privacy">Privacy</Link>
                <Link to="/cookies">Cookies</Link>
                <Link to="/terms">Terms</Link>
                <Link to="/shipping">Shipping</Link>
                <Link to="/returns">Returns</Link>
              </div>
            </div>
          </nav>

          <div className="site-header__actions">
            <button
              type="button"
              className="icon-button"
              aria-label="Search"
              onClick={() => setSearchOpen(true)}
            >
              <FiSearch />
            </button>
            <Link
              className="icon-button icon-button--counted site-header__wishlist"
              to="/shop?view=wishlist"
              aria-label={`Wishlist with ${wishlistCount} items`}
            >
              <FiHeart />
              {wishlistCount > 0 && <span>{wishlistCount}</span>}
            </Link>
            <button
              type="button"
              className="icon-button icon-button--counted"
              aria-label={`Shopping bag with ${itemCount} items`}
              onClick={() => setCartOpen(true)}
            >
              <FiShoppingBag />
              <span>{itemCount}</span>
            </button>
          </div>
        </div>

        <div className={`mobile-menu ${mobileOpen ? "is-open" : ""}`}>
          <nav aria-label="Mobile navigation">
            <span className="mobile-menu__label">Shop</span>
            {shopGroups.flatMap((group) => group.links).map(([label, path]) => (
              <Link key={`${label}-${path}`} to={path}>
                {label} <FiArrowRight />
              </Link>
            ))}
            <span className="mobile-menu__label">Discover</span>
            <Link to="/about">Our story <FiArrowRight /></Link>
            <Link to="/contact">Support <FiArrowRight /></Link>
            <Link to="/faqs">FAQs <FiArrowRight /></Link>
            <Link to="/policies">Policy center <FiArrowRight /></Link>
            <Link to="/privacy">Privacy <FiArrowRight /></Link>
            <Link to="/cookies">Cookies <FiArrowRight /></Link>
            <Link to="/terms">Terms <FiArrowRight /></Link>
          </nav>
        </div>
      </header>

      <div
        className={`drawer-backdrop ${searchOpen || cartOpen ? "is-open" : ""}`}
        onClick={() => {
          setSearchOpen(false);
          setCartOpen(false);
        }}
        aria-hidden="true"
      />

      <section className={`search-panel ${searchOpen ? "is-open" : ""}`} aria-label="Search products">
        <button
          type="button"
          className="icon-button search-panel__close"
          onClick={() => setSearchOpen(false)}
          aria-label="Close search"
        >
          <FiX />
        </button>
        <span>SEARCH THE STORE</span>
        <form onSubmit={submitSearch}>
          <input
            type="search"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Keyboards, mice, collections..."
            aria-label="Search products"
            autoFocus={searchOpen}
          />
          <button type="submit" aria-label="Submit search">
            <FiArrowRight />
          </button>
        </form>
        <div className="search-panel__quick-links">
          <span>Popular:</span>
          <Link to="/shop?category=Keyboards">Keyboards</Link>
          <Link to="/shop?q=Prism">Prism</Link>
          <Link to="/shop?category=Mice">Mice</Link>
        </div>
      </section>

      <aside className={`cart-drawer ${cartOpen ? "is-open" : ""}`} aria-label="Shopping bag">
        <div className="cart-drawer__header">
          <div>
            <span>YOUR BAG</span>
            <strong>{itemCount} {itemCount === 1 ? "ITEM" : "ITEMS"}</strong>
          </div>
          <button type="button" className="icon-button" onClick={() => setCartOpen(false)} aria-label="Close bag">
            <FiX />
          </button>
        </div>

        {cartLines.length === 0 ? (
          <div className="cart-drawer__empty">
            <FiShoppingBag />
            <h2>Your bag is empty</h2>
            <p>Find a centerpiece for your next setup.</p>
            <Link className="site-button site-button--dark" to="/shop">
              SHOP ALL
            </Link>
          </div>
        ) : (
          <>
            <div className="cart-drawer__items">
              {cartLines.map((item) => (
                <article
                  className="mini-cart-item"
                  key={`${item.id}-${item.size}-${item.color}`}
                >
                  <Link to={`/shopDetails/${item.id}`}>
                    <img src={item.product.images[0]} alt={item.product.name} />
                  </Link>
                  <div>
                    <Link to={`/shopDetails/${item.id}`}>
                      {item.product.name}
                    </Link>
                    <span>{item.size} / {item.color}</span>
                    <strong>{formatPrice(item.product.price * item.quantity)}</strong>
                    <div className="mini-cart-item__controls">
                      <button
                        type="button"
                        disabled={item.quantity >= item.product.inventory}
                        onClick={() =>
                          updateQuantity(
                            item.id,
                            item.size,
                            item.color,
                            item.quantity - 1
                          )
                        }
                        aria-label="Decrease quantity"
                      >
                        <FiMinus />
                      </button>
                      <span>{item.quantity}</span>
                      <button
                        type="button"
                        onClick={() =>
                          updateQuantity(
                            item.id,
                            item.size,
                            item.color,
                            item.quantity + 1
                          )
                        }
                        aria-label="Increase quantity"
                      >
                        <FiPlus />
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          removeFromCart(item.id, item.size, item.color)
                        }
                        aria-label={`Remove ${item.product.name}`}
                      >
                        <FiTrash2 />
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
            <div className="cart-drawer__footer">
              <div><span>SUBTOTAL</span><strong>{formatPrice(subtotal)}</strong></div>
              <p>Shipping and taxes calculated at checkout.</p>
              <Link className="site-button site-button--dark" to="/checkout">CHECKOUT</Link>
              <Link className="cart-drawer__view" to="/cartpage">VIEW BAG</Link>
            </div>
          </>
        )}
      </aside>
    </>
  );
}

export default Header;
